import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useScheduledEmailTestMutations,
  useScheduledEmailTests,
} from "@/hooks/use-emailHealth";
import { useGetActiveMailboxesDomain } from "@/hooks/use-mailbox";
import { cn } from "@/lib/utils";
import { IScheduledEmailHealthTest } from "@/types/inboxPlacements.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDays, addHours, format } from "date-fns";
import { CalendarIcon, Clock, Edit, Loader2, Trash2 } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import CustomRecurrenceDialog from "../custom-recurrence/custom-recurrence-dialog";
import {
  getRecurrenceText,
  RecurrenceType,
} from "../custom-recurrence/custom-recurrence-utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { toast } from "sonner";
import Trash from "../icons/trash";
import { AuthProvider } from "@/types/common";

interface ScheduleEmailTestProps {
  isOpen: boolean;
  onClose: () => void;
  type: "placement" | "email-health";
  initialSelectedDomains?: string[];
}

type InitialValueTest =
  | {
      type: "edit";
      initialValues: IScheduledEmailHealthTest;
    }
  | {
      type: "create";
      initialSelectedDomains: string[];
    }
  | null;

// Memoized domain selector component to prevent parent re-renders during search
const DomainSelector = memo(
  ({
    onDomainsChange,
    selectedDomains,
    espType,
  }: {
    onDomainsChange: (domains: string[]) => void;
    selectedDomains: string[];
    espType: AuthProvider;
  }) => {
    const [searchTerm, setSearchTerm] = useState("");

    const {
      isLoading: activeMailboxesLoading,
      data: domainsWithConnectedMailbox,
    } = useGetActiveMailboxesDomain(searchTerm, espType);

    const domainOptions = useMemo(() => {
      return (
        domainsWithConnectedMailbox?.map((domain) => ({
          label: domain.domain,
          value: domain.domain,
        })) || []
      );
    }, [domainsWithConnectedMailbox]);

    return (
      <div className="grid gap-2">
        <div className="flex items-center justify-between gap-2">
          <Label>Send from domain</Label>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {selectedDomains.length} of {domainOptions?.length} selected
            </span>
            {selectedDomains.length === domainOptions.length ? (
              <Button
                variant="outline"
                type="button"
                size="sm"
                className="h-6"
                onClick={() => {
                  onDomainsChange([]);
                }}
              >
                Deselect all
              </Button>
            ) : (
              <Button
                variant="outline"
                type="button"
                size="sm"
                className="h-6"
                onClick={() => {
                  onDomainsChange(domainOptions.map((d) => d.value) || []);
                }}
              >
                Select all
              </Button>
            )}
          </div>
        </div>

        <SearchableMultiSelect
          options={domainOptions}
          loading={activeMailboxesLoading}
          selectedValues={selectedDomains}
          onChange={onDomainsChange}
          placeholder="Select a domain"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          maxHeight={190}
          // forceOpen={}
        />
      </div>
    );
  }
);

DomainSelector.displayName = "DomainSelector";

export default function ScheduleEmailTest({
  isOpen,
  onClose,
  type,
  initialSelectedDomains,
}: ScheduleEmailTestProps) {
  const [activeTab, setActiveTab] = useState("schedule");

  const [initialValueTest, setInitialValueTest] =
    useState<InitialValueTest>(null);

  const { createScheduledTest, isCreating, updateScheduledTest, isUpdating } =
    useScheduledEmailTestMutations(type);

  const handleScheduleTest = async (data: z.infer<typeof formSchema>) => {
    // Combine date and time
    const [hours, minutes] = data.startTime.split(":").map(Number);
    const startDateTime = new Date(data.startDate);
    startDateTime.setHours(hours, minutes, 0, 0);

    if (startDateTime < new Date()) {
      toast.error("Start date and time must be in the future");
      return;
    }

    try {
      if (initialValueTest?.type === "edit") {
        await updateScheduledTest({
          // console.log({
          id: initialValueTest.initialValues._id,
          domains: data.domains,
          scheduleType: data.scheduleType,
          recurrenceType: data.recurrenceType,
          startTime: startDateTime,
          customRecurrence:
            data.recurrenceType === "custom"
              ? data.customRecurrence
              : undefined,
        });
      } else {
        await createScheduledTest({
          // console.log({
          domains: data.domains,
          scheduleType: data.scheduleType,
          recurrenceType: data.recurrenceType,
          startTime: startDateTime,
          espType: data.espType,
          customRecurrence:
            data.recurrenceType === "custom"
              ? data.customRecurrence
              : undefined,
        });
      }

      const eventName =
        initialValueTest?.type === "edit"
          ? "email_health_scheduled_test_updated"
          : "email_health_scheduled_test_created";

      window.cioanalytics?.track(eventName, {
        count: data.domains.length,
        domains: data.domains,
        recurrenceType: data.recurrenceType,
        scheduleType: data.scheduleType,
        startTimestamp: Math.floor(startDateTime.getTime() / 1000),
      });

      setInitialValueTest(null);
      setActiveTab("history");
    } catch (error) {
      console.error("Failed to schedule test:", error);
    }
  };

  const handleEditTest = (test: IScheduledEmailHealthTest) => {
    setInitialValueTest({
      type: "edit",
      initialValues: test,
    });
    setActiveTab("schedule");
  };

  useEffect(() => {
    if (initialSelectedDomains && initialSelectedDomains.length > 0) {
      setInitialValueTest({
        type: "create",
        initialSelectedDomains,
      });
    }
  }, [initialSelectedDomains]);

  // Reset form when dialog opens
  // useEffect(() => {
  //   if (isOpen && !initialValueTest) {
  //     setActiveTab("schedule");
  //   }
  // }, [isOpen, initialValueTest]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] px-0">
        <DialogHeader className="px-6">
          <DialogTitle>Schedule Email Test</DialogTitle>
          <DialogDescription>
            Set up automated email health tests for your domains
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="schedule">Schedule New Test</TabsTrigger>
            <TabsTrigger value="history">Scheduled Tests</TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab === "schedule" && (
          <ScheduleTestForm
            onSubmit={handleScheduleTest}
            initialValues={initialValueTest}
            isLoading={isCreating || isUpdating}
            disabled={false}
            onReset={() => setInitialValueTest(null)}
          />
        )}

        {activeTab === "history" && (
          <ScheduledTestsList onEdit={handleEditTest} />
        )}
      </DialogContent>
    </Dialog>
  );
}

const getDefaultValues = (): z.infer<typeof formSchema> => ({
  domains: [],
  espType: AuthProvider.SMTP,
  scheduleType: "once",
  recurrenceType: "doesnt-repeat",
  startDate: addDays(new Date(), 1),
  startTime: format(addHours(new Date(), 1), "HH:00"),
  customRecurrence: {
    interval: 1,
    unit: "day",
    endType: "never",
  },
});

const setInitialValues = (
  initialValues: InitialValueTest
): z.infer<typeof formSchema> => {
  if (initialValues?.type === "edit") {
    return {
      domains: initialValues.initialValues.domains,
      espType: initialValues.initialValues.espType || AuthProvider.SMTP,
      scheduleType: initialValues.initialValues.schedule.type,
      recurrenceType: initialValues.initialValues.schedule.recurrenceType,
      startDate: initialValues.initialValues.schedule.startTime
        ? new Date(initialValues.initialValues.schedule.startTime)
        : addDays(new Date(), 1),
      startTime: initialValues.initialValues.schedule.startTime
        ? format(
            new Date(initialValues.initialValues.schedule.startTime),
            "HH:mm"
          )
        : format(addHours(new Date(), 1), "HH:00"),
      customRecurrence: initialValues.initialValues.schedule
        .customRecurrence || {
        interval: 1,
        unit: "day",
        endType: "never",
      },
    };
  }

  const defaultValues = getDefaultValues();

  return {
    ...defaultValues,
    domains: initialValues?.initialSelectedDomains || defaultValues.domains,
  };
};

const formSchema = z.object({
  domains: z
    .array(z.string())
    .min(1, { message: "Please select at least one domain" }),
  espType: z.nativeEnum(AuthProvider),
  scheduleType: z.enum(["once", "recurring"]),
  recurrenceType: z.enum([
    "doesnt-repeat",
    "daily",
    "weekly",
    "monthly-by-day",
    "monthly-by-date",
    "annually",
    "weekdays",
    "custom",
  ]),
  startDate: z.date(),
  startTime: z.string().min(1),
  customRecurrence: z.object({
    interval: z.number(),
    unit: z.enum(["day", "week", "month", "year"]),
    endType: z.enum(["never", "on", "after"]),
    endDate: z.date().optional(),
    occurrences: z.number().optional(),
    weekdays: z.array(z.number()).optional(),
  }),
});

const ScheduleTestForm = ({
  onSubmit,
  initialValues,
  isLoading,
  disabled,
  onReset,
}: {
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  initialValues: InitialValueTest;
  isLoading: boolean;
  disabled: boolean;
  onReset: () => void;
}) => {
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: setInitialValues(initialValues),
  });

  const recurrenceType = form.watch("recurrenceType");
  const startDate = form.watch("startDate");
  const customRecurrence = form.watch("customRecurrence");

  const selectedDomains = form.watch("domains");

  // console.log(form.watch());

  return (
    <div className="px-6 pt-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* ESP Type Selection - Must be selected first */}
          <FormField
            control={form.control}
            name="espType"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>Email Service Provider Type</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={(value) => field.onChange(value as AuthProvider)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select ESP type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AuthProvider.SMTP}>SMTP - Standard SMTP Server</SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE}>Google - Gmail/Google Workspace</SelectItem>
                      <SelectItem value={AuthProvider.MICROSOFT}>Microsoft - Outlook/Office 365</SelectItem>
                      <SelectItem value={AuthProvider.AWS_API}>Amazon SES</SelectItem>
                      <SelectItem value={AuthProvider.SENDGRID_API}>SendGrid</SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE_WORKSPACE_SMTP}>Google Workspace SMTP - Google Workspace SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Select the email service provider type to see available domains
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Domain Selection - Only show after ESP type is selected */}
          {form.watch("espType") && (
            <FormField
              control={form.control}
              name="domains"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                    {/* @ts-ignore - field.value may be undefined */}
                    <DomainSelector
                      selectedDomains={field.value || []}
                      onDomainsChange={field.onChange}
                      espType={form.watch("espType") as AuthProvider}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Show message when no ESP type is selected */}
          {!form.watch("espType") && (
            <div className="p-4 text-center text-muted-foreground border rounded-md">
              Please select an Email Service Provider type above to see available domains
            </div>
          )}

          {/* Rest of the form fields - only show when domains are selected */}
          {form.watch("espType") && form.watch("domains").length > 0 && (
            <>
              <FormField
                control={form.control}
                name="recurrenceType"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Select Recurrence Type</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(value: RecurrenceType) => {
                          console.log("value", value);
                          field.onChange(value);
                          form.setValue(
                            "scheduleType",
                            value === "doesnt-repeat" ? "once" : "recurring"
                          );
                          if (value === "custom") {
                            console.log("custom");
                            setShowCustomDialog(true);
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue>
                            {getRecurrenceText(
                              recurrenceType,
                              customRecurrence,
                              startDate
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="doesnt-repeat">
                            Doesn't repeat
                          </SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">
                            Weekly on {format(startDate, "EEEE")}
                          </SelectItem>
                          <SelectItem value="monthly-by-day">
                            Monthly on the first {format(startDate, "EEEE")}
                          </SelectItem>
                          <SelectItem value="monthly-by-date">
                            Monthly on day {format(startDate, "d")}
                          </SelectItem>
                          <SelectItem value="annually">
                            Annually on {format(startDate, "MMMM d")}
                          </SelectItem>
                          <SelectItem value="weekdays">
                            Every weekday (Monday to Friday)
                          </SelectItem>
                          <SelectItem value="custom">Custom...</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Select Start Date</FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal text-sm",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value
                              ? format(field.value, "MMMM d, yyyy")
                              : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => date && field.onChange(date)}
                            initialFocus
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Select Start Time</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full">
                          <div className="flex items-center">
                            <Clock className="mr-2 h-4 w-4 text-foreground" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <ScrollArea className="h-[200px]">
                            {Array.from({ length: 24 * 4 }, (_, i) => {
                              const hour = Math.floor(i / 4);
                              const minute = (i % 4) * 15;
                              const time = `${hour
                                .toString()
                                .padStart(2, "0")}:${minute
                                .toString()
                                .padStart(2, "0")}`;
                              const period = hour >= 12 ? "PM" : "AM";
                              const displayHour =
                                hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                              return (
                                <SelectItem key={time} value={time}>
                                  {`${displayHour}:${minute
                                    .toString()
                                    .padStart(2, "0")} ${period}`}
                                </SelectItem>
                              );
                            })}
                          </ScrollArea>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </form>
      </Form>

      <DialogFooter className="mt-4">
        <Button
          variant="outline"
          onClick={() => {
            form.reset(getDefaultValues());
            onReset();
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={isLoading || disabled}
        >
          {isLoading
            ? "Scheduling..."
            : initialValues?.type === "edit"
            ? "Update Test"
            : "Schedule Test"}
        </Button>
      </DialogFooter>

      <CustomRecurrenceDialog
        open={showCustomDialog}
        onClose={() => setShowCustomDialog(false)}
        customRecurrence={customRecurrence}
        setCustomRecurrence={(customRecurrence) => {
          form.setValue("customRecurrence", customRecurrence);
        }}
        startDate={startDate}
      />
    </div>
  );
};

const ScheduledTestsList = ({
  onEdit,
}: {
  onEdit: (test: IScheduledEmailHealthTest) => void;
}) => {
  const { deleteScheduledTest, isDeleting } =
    useScheduledEmailTestMutations("email-health");

  const { data: scheduledTests, isLoading: isLoadingTests } =
    useScheduledEmailTests("email-health");

  const handleDeleteTest = async (id: string) => {
    try {
      await deleteScheduledTest(id);
    } catch (error) {
      console.error("Failed to delete test:", error);
    }
  };

  return (
    <ScrollArea className="h-[450px] px-6 mt-4">
      {isLoadingTests ? (
        <div className="text-center py-2">Loading scheduled tests...</div>
      ) : !scheduledTests ||
        !Array.isArray(scheduledTests) ||
        scheduledTests.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No scheduled tests found
        </div>
      ) : (
        <div className="space-y-4">
          {scheduledTests.map((test: IScheduledEmailHealthTest) => (
            <div key={test._id} className="border rounded-lg p-4 space-y-3 ">
              <div className="flex justify-between items-start">
                <h4 className="font-medium">
                  {test.domains?.length > 1
                    ? `${test.domains[0]} +${test.domains.length - 1} more`
                    : test.domains[0]}
                </h4>

                <Badge
                  variant="soft"
                  color={
                    test.status === "active"
                      ? "blue"
                      : test.status === "completed"
                      ? "green"
                      : test.status === "failed"
                      ? "red"
                      : "default"
                  }
                  className="ml-2 capitalize"
                >
                  {test.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-wrap gap-1 col-span-full">
                  {test.domains.map((domain: string) => (
                    <Badge
                      key={domain}
                      variant="outline"
                      className="font-normal"
                    >
                      {domain}
                    </Badge>
                  ))}
                </div>

                <div className="text-sm space-y-1">
                  <span className="font-medium">Recurrence: </span>
                  <div className="text-sm text-muted-foreground">
                    {getRecurrenceText(
                      test.schedule.recurrenceType,
                      test.schedule.customRecurrence,
                      new Date(test.schedule.startTime)
                    )}
                  </div>
                </div>

                <div className="text-sm space-y-1">
                  <span className="font-medium">Started At: </span>
                  <div className="text-sm text-muted-foreground">
                    {format(
                      new Date(test.schedule.startTime),
                      "MMMM d, yyyy h:mm a"
                    )}
                  </div>
                </div>

                <div className="text-sm space-y-1">
                  <span className="font-medium">Last Run: </span>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(test.lastRun), "MMMM d, yyyy h:mm a")}
                  </div>
                </div>
                <div className="text-sm space-y-1">
                  <span className="font-medium">Next Run: </span>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(test.nextRun), "MMMM d, yyyy h:mm a")}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(test)}
                >
                  <Edit className="h-3.5 w-3.5 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteTest(test._id)}
                  disabled={isDeleting}
                  className="delete-button group"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  );
};
